import type { SessionContext } from "../types";

/**
 * Base mock session context with common fields
 */
const createBaseMockContext = (): Omit<SessionContext, "gdprSettings"> => ({
	userId: "mock-user-id",
	workspaceId: "mock-workspace-id",
	projectId: "mock-project-id",
	campaignId: "mock-campaign-id",
	landingPageId: "mock-landing-page-id",
	session: {
		sessionId: `mock-session-${Date.now()}`,
	},
	campaignEnvironment: "preview",
	apiBaseUrl: "https://api.mock.firebuzz.dev",
	abTestId: null,
	abTestVariantId: null,
	botDetection: {
		score: 0.1,
		corporateProxy: false,
		verifiedBot: false,
	},
});

/**
 * Creates a mock session context for an EU user requiring GDPR consent
 */
export const createMockEUContext = (): SessionContext => ({
	...createBaseMockContext(),
	gdprSettings: {
		isEnabled: true,
		isRequiredConsent: true,
		isEU: true,
		isCalifornian: false,
		isIncludedCountry: true,
		isLocalizationEnabled: true,
		countryCode: "DE",
		language: "de-DE",
		isRespectDNTEnabled: true,
		privacyPolicyUrl: "https://example.com/privacy",
		termsOfServiceUrl: "https://example.com/terms",
	},
});

/**
 * Creates a mock session context for a US user (no consent required)
 */
export const createMockUSContext = (): SessionContext => ({
	...createBaseMockContext(),
	gdprSettings: {
		isEnabled: false,
		isRequiredConsent: false,
		isEU: false,
		isCalifornian: false,
		isIncludedCountry: false,
		isLocalizationEnabled: false,
		countryCode: "US",
		language: "en-US",
		isRespectDNTEnabled: false,
		privacyPolicyUrl: "https://example.com/privacy",
		termsOfServiceUrl: "https://example.com/terms",
	},
});

/**
 * Creates a mock session context for a Californian user (CCPA)
 */
export const createMockCalifornianContext = (): SessionContext => ({
	...createBaseMockContext(),
	gdprSettings: {
		isEnabled: true,
		isRequiredConsent: true,
		isEU: false,
		isCalifornian: true,
		isIncludedCountry: true,
		isLocalizationEnabled: false,
		countryCode: "US",
		language: "en-US",
		isRespectDNTEnabled: true,
		privacyPolicyUrl: "https://example.com/privacy",
		termsOfServiceUrl: "https://example.com/terms",
	},
});

/**
 * Creates a mock session context for a user with Do Not Track enabled
 */
export const createMockContextWithDNT = (): SessionContext => ({
	...createBaseMockContext(),
	gdprSettings: {
		isEnabled: true,
		isRequiredConsent: true,
		isEU: true,
		isCalifornian: false,
		isIncludedCountry: true,
		isLocalizationEnabled: true,
		countryCode: "FR",
		language: "fr-FR",
		isRespectDNTEnabled: true,
		privacyPolicyUrl: "https://example.com/privacy",
		termsOfServiceUrl: "https://example.com/terms",
	},
});

/**
 * Creates a mock session context with custom GDPR settings
 */
export const createMockContextWithCustomSettings = (
	customSettings: Partial<SessionContext["gdprSettings"]>,
): SessionContext => ({
	...createBaseMockContext(),
	gdprSettings: {
		isEnabled: true,
		isRequiredConsent: true,
		isEU: false,
		isCalifornian: false,
		isIncludedCountry: true,
		isLocalizationEnabled: true,
		countryCode: "GB",
		language: "en-GB",
		isRespectDNTEnabled: false,
		privacyPolicyUrl: "https://example.com/privacy",
		termsOfServiceUrl: "https://example.com/terms",
		...customSettings,
	},
});

/**
 * Creates a mock session context for production environment
 */
export const createMockProductionContext = (): SessionContext => ({
	...createBaseMockContext(),
	campaignEnvironment: "production",
	gdprSettings: {
		isEnabled: true,
		isRequiredConsent: true,
		isEU: true,
		isCalifornian: false,
		isIncludedCountry: true,
		isLocalizationEnabled: true,
		countryCode: "IT",
		language: "it-IT",
		isRespectDNTEnabled: true,
		privacyPolicyUrl: "https://example.com/privacy",
		termsOfServiceUrl: "https://example.com/terms",
	},
});


